/**
 * messageTemplatesAdmin.service.ts — Admin > Forms & Templates > SMS Templates
 * and Email Templates: the account's canned messages and their categories,
 * through the API v5 calls the corporate dashboard's pages use. Both stores
 * have the same shape, so one set of calls serves both:
 *
 *   SMS   — `sms` (smsID, name, description, message, smsCategoryID, sOrder)
 *           + `sms_category`, at /sms-templates and /sms-template-categories;
 *   Email — `email_content` (emailContentID, name, description, subject,
 *           content (HTML), emailContentCategoryID, eOrder) + its categories,
 *           at /email-templates and /email-template-categories. NOT the
 *           marketing store (/marketing/email-templates).
 *
 * Templates: list (every row, deleted ones too), create, update, delete (soft:
 * active 0), restore, and reorder — POST /…/reorder {templates, categoryID}
 * writes the target category's whole order and files the listed templates
 * under it (how a drag across categories saves). Categories: list (live
 * ones), add, rename, delete (soft; the API has no restore). Placeholders are
 * the `${PatientFirst}` codes the legacy senders replace (Insert Shortcode);
 * corp's "{{name}}" hint text was wrong. Writes need Admin Access.
 */
import { apiClient } from '@/services/apiClient';
import type { ApiResponse } from '@/types/api.types';

export type MessageTemplateKind = 'sms' | 'email';

interface KindConfig {
  path: string;
  categoryPath: string;
  idKey: string;
  categoryIdKey: string;
  orderKey: string;
  categoryOrderKey: string;
}

const KINDS: Record<MessageTemplateKind, KindConfig> = {
  sms: {
    path: '/sms-templates',
    categoryPath: '/sms-template-categories',
    idKey: 'smsID',
    categoryIdKey: 'smsCategoryID',
    orderKey: 'sOrder',
    categoryOrderKey: 'scOrder',
  },
  email: {
    path: '/email-templates',
    categoryPath: '/email-template-categories',
    idKey: 'emailContentID',
    categoryIdKey: 'emailContentCategoryID',
    orderKey: 'eOrder',
    categoryOrderKey: 'ecOrder',
  },
};

export interface MessageTemplate {
  id: number;
  name: string;
  description: string;
  /** Null for none (0). */
  categoryId: number | null;
  order: number;
  active: boolean;
  /** False when its category is deleted; null when unknown. */
  categoryActive: boolean | null;
  /** SMS: the text. */
  message: string;
  /** Email only. */
  subject: string;
  /** Email only: the HTML body. */
  content: string;
}

export interface MessageTemplateCategory {
  id: number;
  name: string;
  order: number;
}

const isActive = (v: unknown) => v !== false && v !== 0 && v !== '0';

export function toMessageTemplate(kind: MessageTemplateKind, row: Record<string, unknown>): MessageTemplate {
  const k = KINDS[kind];
  const category = (row.category ?? null) as Record<string, unknown> | null;
  const categoryId = Number(row[k.categoryIdKey] ?? 0) || null;
  return {
    id: Number(row[k.idKey] ?? 0),
    name: String(row.name ?? ''),
    description: String(row.description ?? ''),
    categoryId,
    order: Number(row[k.orderKey] ?? 0) || 0,
    active: isActive(row.active),
    categoryActive: category && 'active' in category ? isActive(category.active) : null,
    message: String(row.message ?? ''),
    subject: String(row.subject ?? ''),
    content: String(row.content ?? ''),
  };
}

function toCategory(kind: MessageTemplateKind, row: Record<string, unknown>): MessageTemplateCategory {
  const k = KINDS[kind];
  return {
    id: Number(row[k.categoryIdKey] ?? row.id ?? 0),
    name: String(row.name ?? ''),
    order: Number(row[k.categoryOrderKey] ?? 0) || 0,
  };
}

function rows(data: unknown): Record<string, unknown>[] {
  return Array.isArray(data) ? (data as Record<string, unknown>[]) : [];
}

/**
 * Every template of the account, deleted ones too. Without `account_ids` the
 * API answers a plain page (paging fields at the top level, not in `meta` —
 * which is why corp showed no paging for one account); every page is read.
 */
export async function listMessageTemplates(kind: MessageTemplateKind): Promise<MessageTemplate[]> {
  const out: MessageTemplate[] = [];
  for (let page = 1; ; page++) {
    const r = await apiClient.get<{ data?: unknown; current_page?: number; last_page?: number }>(KINDS[kind].path, {
      params: { page, limit: 1000, show_inactive: true },
    });
    out.push(...rows(r.data?.data).map((row) => toMessageTemplate(kind, row)));
    const last = Number(r.data?.last_page ?? 1) || 1;
    if (page >= last) return out;
  }
}

/** The live categories. Email's would carry every template's HTML too; `with_templates=0` leaves them out. */
export function listMessageTemplateCategories(kind: MessageTemplateKind): Promise<MessageTemplateCategory[]> {
  return apiClient
    .get<ApiResponse<Record<string, unknown>[]>>(KINDS[kind].categoryPath, { params: { with_templates: 0 } })
    .then((r) => rows(r.data?.data).map((row) => toCategory(kind, row)));
}

export function createMessageTemplateCategory(kind: MessageTemplateKind, name: string): Promise<MessageTemplateCategory> {
  return apiClient
    .post<ApiResponse<Record<string, unknown>>>(KINDS[kind].categoryPath, { name: name.trim() })
    .then((r) => toCategory(kind, r.data?.data ?? {}));
}

export function renameMessageTemplateCategory(kind: MessageTemplateKind, id: number, name: string): Promise<void> {
  return apiClient.patch(`${KINDS[kind].categoryPath}/${id}`, { name: name.trim() }).then(() => undefined);
}

export function deleteMessageTemplateCategory(kind: MessageTemplateKind, id: number): Promise<void> {
  return apiClient.delete(`${KINDS[kind].categoryPath}/${id}`).then(() => undefined);
}

export interface MessageTemplateDraft {
  name: string;
  description: string;
  /** Sent on create only: an edit leaves the category to the board (drag). */
  categoryId?: number;
  message: string;
  subject: string;
  content: string;
}

/** The write body. Name / description / subject are trimmed (as corp); the message and HTML are sent as typed. */
export function messageTemplatePayload(kind: MessageTemplateKind, d: MessageTemplateDraft): Record<string, unknown> {
  const k = KINDS[kind];
  const base = {
    name: d.name.trim(),
    description: d.description.trim(),
    ...(d.categoryId !== undefined ? { [k.categoryIdKey]: d.categoryId } : {}),
  };
  return kind === 'sms' ? { ...base, message: d.message } : { ...base, subject: d.subject.trim(), content: d.content };
}

export function createMessageTemplate(kind: MessageTemplateKind, d: MessageTemplateDraft): Promise<MessageTemplate> {
  return apiClient
    .post<ApiResponse<Record<string, unknown>>>(KINDS[kind].path, messageTemplatePayload(kind, d))
    .then((r) => toMessageTemplate(kind, r.data?.data ?? {}));
}

export function updateMessageTemplate(kind: MessageTemplateKind, id: number, d: MessageTemplateDraft): Promise<void> {
  return apiClient.patch(`${KINDS[kind].path}/${id}`, messageTemplatePayload(kind, d)).then(() => undefined);
}

export function deleteMessageTemplate(kind: MessageTemplateKind, id: number): Promise<void> {
  return apiClient.delete(`${KINDS[kind].path}/${id}`).then(() => undefined);
}

export function restoreMessageTemplate(kind: MessageTemplateKind, id: number): Promise<void> {
  return apiClient.post(`${KINDS[kind].path}/${id}/restore`).then(() => undefined);
}

/** The category's whole order after a drop; the listed templates are filed under it. */
export function reorderMessageTemplates(kind: MessageTemplateKind, categoryId: number, ids: number[]): Promise<void> {
  return apiClient.post(`${KINDS[kind].path}/reorder`, { templates: ids, categoryID: categoryId }).then(() => undefined);
}

/** True when an HTML body holds anything: text, or an image / table / rule (corp counted only text). */
export function htmlHasContent(html: string): boolean {
  if (/<(img|table|hr|video|iframe)\b/i.test(html)) return true;
  return html.replace(/<[^>]*>/g, '').replace(/&nbsp;/g, ' ').trim() !== '';
}
